import React, { useCallback, useEffect, useState } from "react";
import { logError } from "../lib/log";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, API, apiError } from "../lib/api";
import { Plus, Trash2, Copy, Share2, School } from "lucide-react";
import { TeacherAssignments } from "../components/Assignments";
import { BlockedWordsEditor } from "../components/BlockedWords";
import { AbsenceWatch } from "../components/AbsenceWatch";
import { AiQuizDraft } from "../components/AiQuizDraft";

const newQuestion = () => ({ id: crypto.randomUUID(), q: "", options: ["", "", "", ""], answer: 0, explanation: "" });

function QuizBuilder({ onCreated, initial }) {
  const [title, setTitle] = useState(initial ? `Copy of ${initial.title}` : "");
  const [questions, setQuestions] = useState(initial ? initial.questions.map((q) => ({ ...newQuestion(), ...q, options: [...q.options] })) : [newQuestion()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [drafted, setDrafted] = useState(null);

  const applyDraft = (d) => {
    setTitle(d.title);
    setQuestions(d.questions.map((q) => ({ ...newQuestion(), ...q, options: [...q.options] })));
    setDrafted(d.model);
    setErr(null);
  };

  const update = (i, patch) => setQuestions(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const updateOpt = (i, oi, v) => update(i, { options: questions[i].options.map((o, k) => (k === oi ? v : o)) });
  const add = () => setQuestions([...questions, newQuestion()]);
  const remove = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const submit = async () => {
    setErr(null);
    if (!title.trim()) return setErr("Give the quiz a title.");
    for (const q of questions) {
      if (!q.q.trim() || q.options.some((o) => !o.trim())) return setErr("Fill in every question and option.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/teacher/quizzes", { title, questions: questions.map(({ id, ...q }) => q) });
      onCreated(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create the quiz.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 rounded-md border border-white/10 bg-black/40 p-6" data-testid="quiz-builder">
      <AiQuizDraft onDraft={applyDraft} />
      {drafted && (
        <div className="rounded-md border border-lime-400/40 bg-lime-500/5 p-2 text-xs text-lime-200" data-testid="ai-draft-applied">
          Drafted by {drafted} — review the questions, tweak anything, then save.
        </div>
      )}
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Quiz title (e.g. Chapter 3 pop quiz)"
        data-testid="builder-title"
        className="w-full rounded-md border border-white/10 bg-black/60 px-4 py-3 text-lg font-semibold text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
      />
      {questions.map((q, i) => (
        <div key={q.id} className="rounded-md border border-white/10 bg-black/60 p-4" data-testid={`builder-q-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-cyan-300">Q{i + 1}</span>
            {questions.length > 1 && (
              <button onClick={() => remove(i)} className="text-slate-500 hover:text-red-300" data-testid={`builder-remove-${i}`}><Trash2 size={14} /></button>
            )}
          </div>
          <input
            value={q.q}
            onChange={(e) => update(i, { q: e.target.value })}
            placeholder="Question text"
            className="mt-2 w-full rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
          />
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {q.options.map((opt, oi) => (
              <label key={`${q.id}-${oi}`} className={`flex items-center gap-2 rounded-md border p-2 text-sm ${q.answer === oi ? "border-lime-400/50 bg-lime-500/5" : "border-white/10 bg-black/50"}`}>
                <input type="radio" checked={q.answer === oi} onChange={() => update(i, { answer: oi })} data-testid={`builder-q-${i}-correct-${oi}`} className="accent-lime-400" />
                <input
                  value={opt}
                  onChange={(e) => updateOpt(i, oi, e.target.value)}
                  placeholder={`Option ${oi + 1}`}
                  className="flex-1 bg-transparent text-slate-200 focus:outline-none"
                />
              </label>
            ))}
          </div>
          <input
            value={q.explanation}
            onChange={(e) => update(i, { explanation: e.target.value })}
            placeholder="Optional: explain the answer"
            className="mt-3 w-full rounded-md border border-white/10 bg-black/60 px-3 py-2 text-xs text-slate-300 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
          />
        </div>
      ))}
      <button onClick={add} data-testid="builder-add-q" className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs hover:bg-white/10">
        <Plus size={14} /> Add question
      </button>
      {err && <div className="rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="builder-error">{err}</div>}
      <button
        onClick={submit}
        disabled={busy}
        data-testid="builder-save"
        className="w-full rounded-md bg-cyan-500 px-4 py-3 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-50"
      >
        {busy ? "Creating…" : "Create quiz & get shareable link"}
      </button>
    </div>
  );
}

export default function Teacher() {
  const { user, loading } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderInitial, setBuilderInitial] = useState(null);
  const duplicateQuiz = (q) => {
    setBuilderInitial(q);
    setShowBuilder(true);
    setJustCreated(null);
    setTimeout(() => document.querySelector("[data-testid=quiz-builder]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(null);
  const [classCode, setClassCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  const refresh = useCallback(() => api.get("/teacher/quizzes").then((r) => setQuizzes(r.data.quizzes)).catch((e) => logError("Quiz list failed", e)), []);
  useEffect(() => {
    if (user?.role === "teacher") {
      refresh();
      api.get("/teacher/me").then((r) => setClassCode(r.data.class_code)).catch((e) => logError("Teacher profile failed", e));
    }
  }, [user, refresh]);

  const [deleteErr, setDeleteErr] = useState(null);
  const deleteQuiz = async (q) => {
    if (!window.confirm(`Delete "${q.title}"? Student attempts and its leaderboard will be removed too.`)) return;
    setDeleteErr(null);
    try {
      await api.delete(`/teacher/quizzes/${q.id}`);
      setQuizzes((list) => list.filter((x) => x.id !== q.id));
      if (justCreated?.id === q.id) setJustCreated(null);
    } catch (e) {
      setDeleteErr(apiError(e, "Could not delete quiz"));
    }
  };

  const copyClassCode = async () => {
    if (!classCode) return;
    await navigator.clipboard.writeText(classCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1500);
  };

  if (loading) return <div className="text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "teacher") {
    return (
      <div className="rounded-md border border-amber-400/40 bg-amber-500/10 p-6 text-amber-200" data-testid="teacher-not-allowed">
        This page is for teacher accounts. Register a new account with the Teacher role.
      </div>
    );
  }

  const shareLink = (qid) => `${window.location.origin}/c/${qid}`;
  const copy = async (qid) => {
    await navigator.clipboard.writeText(shareLink(qid));
    setCopied(qid);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-8" data-testid="teacher-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs uppercase tracking-widest text-cyan-300">
          <School size={12} /> Teacher mode
        </div>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Your custom quizzes</h1>
        <p className="mt-2 text-slate-400">Build a quiz, share the link with your class.</p>
      </div>

      {classCode && (
        <div className="rounded-md border border-amber-400/40 bg-amber-500/5 p-5" data-testid="class-code-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300">Your class code</div>
              <div className="font-display mt-1 text-lg font-semibold text-white">Students enter this once to join your roster</div>
            </div>
            <button onClick={copyClassCode} data-testid="copy-class-code" className="flex items-center gap-3 rounded-md border border-amber-400/40 bg-black/60 px-4 py-3 hover:bg-black/80">
              <span className="font-mono-num text-2xl font-bold tracking-widest text-amber-200">{classCode}</span>
              <Copy size={14} className="text-amber-300" />
              {codeCopied && <span className="text-xs text-lime-300">✓</span>}
            </button>
          </div>
        </div>
      )}

      {!showBuilder && (
        <div className="flex flex-wrap gap-3">
          <button onClick={() => { setBuilderInitial(null); setShowBuilder(true); }} data-testid="new-quiz-btn" className="inline-flex items-center gap-2 rounded-md bg-cyan-500 px-4 py-3 text-sm font-semibold text-black hover:bg-cyan-400">
            <Plus size={14} /> New quiz
          </button>
          <Link to="/teacher/roster" data-testid="teacher-roster-link" className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium hover:bg-white/10">
            View class roster →
          </Link>
        </div>
      )}

      {showBuilder && (
        <QuizBuilder
          key={builderInitial?.id || "new"}
          initial={builderInitial}
          onCreated={(q) => {
            setJustCreated(q);
            setShowBuilder(false);
            setBuilderInitial(null);
            refresh();
          }}
        />
      )}

      {justCreated && (
        <div className="rounded-md border border-lime-400/40 bg-lime-500/5 p-5" data-testid="just-created">
          <div className="text-[10px] uppercase tracking-widest text-lime-300">Shareable link ready</div>
          <div className="font-display mt-1 text-lg text-white">{justCreated.title}</div>
          <div className="mt-3 flex items-center gap-2">
            <code className="font-mono-num flex-1 truncate rounded-md border border-white/10 bg-black/60 px-3 py-2 text-xs text-cyan-200">{shareLink(justCreated.id)}</code>
            <button onClick={() => copy(justCreated.id)} className="rounded-md border border-white/10 bg-white/5 p-2 hover:bg-white/10"><Copy size={14} /></button>
          </div>
        </div>
      )}

      <TeacherAssignments />

      <AbsenceWatch />

      <BlockedWordsEditor />

      <div>
        <h2 className="font-display text-xl font-semibold">All your quizzes</h2>
        {deleteErr && <div className="mt-3 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="quiz-delete-error">{deleteErr}</div>}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {quizzes.map((q) => (
            <div key={q.id} className="rounded-md border border-white/10 bg-black/40 p-4" data-testid={`quiz-row-${q.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-white">{q.title}</div>
                  <div className="text-xs text-slate-500">{q.questions.length} questions</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/teacher/quizzes/${q.id}/leaderboard`} data-testid={`leaderboard-link-${q.id}`} className="rounded-md border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-200 hover:bg-amber-500/20">Leaderboard</Link>
                  <Link to={`/c/${q.id}`} className="rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs hover:bg-white/10">Open</Link>
                  <button onClick={() => copy(q.id)} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs hover:bg-white/10">
                    <Share2 size={12} /> {copied === q.id ? "Copied!" : "Share"}
                  </button>
                  <button onClick={() => duplicateQuiz(q)} data-testid={`quiz-duplicate-${q.id}`} title="Duplicate as a new quiz" className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs hover:bg-white/10">
                    <Copy size={12} /> Duplicate
                  </button>
                  <button onClick={() => deleteQuiz(q)} data-testid={`quiz-delete-${q.id}`} title="Delete quiz" className="icon-btn rounded-md border border-white/10 bg-white/5 p-1.5 text-slate-400 hover:border-red-400/40 hover:bg-red-500/10 hover:text-red-300">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {quizzes.length === 0 && !showBuilder && (
            <div className="col-span-full rounded-md border border-white/10 bg-black/40 p-6 text-center text-sm text-slate-500">No quizzes yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
